import { getInputsFromEvaluatorLog } from "@/lib/evaluator-log-utils";
import { parseLogOutputAsMessage } from "@/lib/messages";
import { classNames } from "@/lib/utils";
import { ChatMessage, ChatMessageData } from "@/services/promptEditor.service";
import { getFileFromLog, getPromptFromLog, Log, TemplateToolOutput } from "@/types/app/log";
import { Toggle } from "@components/atoms/Toggle/Toggle";
import { AutoFormattedText, LanguageSelectLocation } from "@components/library/FormattedText";
import KeyboardShortcut from "@components/library/KeyboardShortcut";
import { PageSectionHeader } from "@components/library/PageSectionHeader";
import Tooltip from "@components/library/Tooltip";
import Tag from "@components/library/v2/Tag";
import { Attachment } from "@components/PromptEditor/Chat/Message";
import { FormattedPrompt } from "@components/PromptEditor/FormattedPrompt";
import { getMessagesAfterTemplate } from "@components/PromptEditor/History/utils";
import { InputTag, TagWithTitle, ToolTag } from "@components/PromptEditor/VariableTags";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { ChatMessages } from "../ChatMessages";
import {
  DescriptionDetails,
  DescriptionDetailsTruncated,
  DescriptionList,
  DescriptionTerm,
  TruncatedExpandable,
} from "../DescriptionList";
import { JudgmentTags } from "../Evaluations/EvaluationResult";
import { None } from "../Tables/Logs/LogCells";
import { EntityLabel } from "@components/library/Entities/EntityLabel";
import { getFileHref } from "@/lib/path-utils";
import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";

interface LogContentProps {
  log: Log;
  /** Display content as a vertical list where:
   * - Each item appears on its own line
   *
   * For example:
   * Assistant
   * [message content]
   * User
   * [message content]
   *
   * Instead of the default horizontal layout:
   * Assistant [message content]
   * User [message content]
   */
  displayAsList?: boolean;
  /** Whether to show the Log Inputs */
  showInputs?: boolean;
}

// Forces items to display in a single-column vertical layout (list view)
const SHOW_AS_LIST_STYLE = { gridTemplateColumns: "auto", gridTemplateRows: "auto" };

/** Render the content of a log e.g. the inputs, messages and the output.
 *
 * This is smart enough to handle both completions and chats, and whether or not to show the template.
 */
export const LogContent = ({ log, displayAsList = false, showInputs = true }: LogContentProps) => {
  const { output, error, stdout } = log;
  const messages = "messages" in log ? log.messages : null;
  // Markdown is a half-baked feature still.
  const [showAsMarkdown, setShowAsMarkdown] = useState(false);

  const [renderTemplate, setRenderTemplate] = useState(false);

  // Attempt to use the model config for an LLM Evaluator.
  // Log is a non-Evaluator Log (as those are rendered as Evaluation Results separately
  // unless it's an LLM Evaluator Log). For LLM Evaluator Logs, we use this to render the inputs
  // and so have to pull out the underlying model config to render templates correctly.
  const file = getFileFromLog(log);
  const prompt = getPromptFromLog(log);
  const templateLanguage = prompt?.template_language || "default";

  // Inferred endpoint based on the Log
  // Currently used for 2 separate things:
  // - Whether to show "Show chat template" or "Show prompt template" in tooltip
  // - Whether to show output_message or output-as-a-message.
  const inferredEndpoint = prompt ? prompt.endpoint : messages === null ? "completion" : "chat";

  // If this is an evaluator log, we need to get the inputs from the parent log
  const inputs =
    file.type === "evaluator" && "parent" in log && log.parent
      ? getInputsFromEvaluatorLog({
          log: log.parent,
          evaluatorLog: log,
        })
      : log.inputs;

  const toolName = file.type === "tool" && "tool" in log ? log.tool.function?.name : undefined;

  const messagesAfterTemplate =
    messages && prompt && prompt.endpoint === "chat" && prompt.template
      ? getMessagesAfterTemplate(messages, prompt.template as ChatMessageData[])
      : messages;

  const messagesInTemplate = (prompt && prompt.endpoint === "chat" && (prompt.template as ChatMessageData[])) || null;

  // Handle the pre 2023-11-27 logs where we didn't have output_message and just put that content
  // in the output
  const outputMessage: ChatMessage | null =
    // Show `output_message` if provided, even if inferred `type` is not chat.
    "output_message" in log
      ? log.output_message
      : inferredEndpoint === "chat"
        ? // If inferred "type" is chat and there's no `output_message`, try to show `output` as a message.
          output !== null
          ? parseLogOutputAsMessage(output || "")
          : null
        : null;

  useHotkeys("m", () => setShowAsMarkdown(!showAsMarkdown), { keydown: true });
  useHotkeys("t", () => setRenderTemplate(!renderTemplate), { keydown: true });
  return (
    <>
      <PageSectionHeader
        title="Content"
        size="sm"
        actionsTray={
          prompt ? (
            <>
              {/* Probably want to move this an inline menu, but given only one item in it, felt better to expose it */}
              <Tooltip
                content={
                  <div className="flex items-center gap-6">
                    Show the inputs in the context of the template
                    <KeyboardShortcut>T</KeyboardShortcut>
                  </div>
                }
              >
                <span>
                  <Toggle
                    size="md"
                    toggleOnRight
                    label={<div className="flex items-center gap-6">Show Template</div>}
                    enabled={renderTemplate}
                    setEnabled={setRenderTemplate}
                  />
                </span>
              </Tooltip>
            </>
          ) : (
            <></>
          )
        }
      />
      <div className="flex flex-col gap-16">
        {!renderTemplate ? (
          showInputs ? (
            <>
              {/* Template tools */}
              {log.template_tool_outputs && log.template_tool_outputs.length > 0 && (
                <LogToolOutputsDisplay
                  templateToolOutputs={log.template_tool_outputs}
                  style={displayAsList ? SHOW_AS_LIST_STYLE : undefined}
                />
              )}
              {!isEmpty(inputs) && (
                <>
                  {/* Inputs */}
                  <LogInputsDisplay inputs={inputs} style={displayAsList ? SHOW_AS_LIST_STYLE : undefined} />
                </>
              )}
            </>
          ) : null
        ) : (
          <>
            <LogTemplate log={log} />
            {messagesInTemplate ? (
              <ChatMessages
                messages={messagesInTemplate}
                style={displayAsList ? SHOW_AS_LIST_STYLE : undefined}
                populateTemplate
                variables={inputs}
                templateToolOutputs={log.template_tool_outputs}
                formatContent={true}
                templateLanguage={templateLanguage}
              />
            ) : null}
          </>
        )}

        {/* Messages (which is obviously null if this is a completion Log) */}
        {messagesAfterTemplate ? (
          <ChatMessages
            messages={messagesAfterTemplate}
            style={displayAsList ? { gridTemplateColumns: "auto", gridTemplateRows: "auto", gap: "8px" } : undefined}
            formatContent={true}
            templateLanguage={templateLanguage}
          />
        ) : null}

        {/* Output */}
        {outputMessage ? (
          <ChatMessages
            messages={[outputMessage]}
            style={displayAsList ? SHOW_AS_LIST_STYLE : undefined}
            formatContent={true}
            templateLanguage={templateLanguage}
            toolName={toolName}
          />
        ) : (
          <DescriptionList style={displayAsList ? SHOW_AS_LIST_STYLE : undefined}>
            <DescriptionTerm>
              <Tag>Output</Tag>
            </DescriptionTerm>
            {output === null ? (
              <DescriptionDetails>
                <None />
              </DescriptionDetails>
            ) : (
              <DescriptionDetails>
                <AutoFormattedText text={output} />
              </DescriptionDetails>
            )}
          </DescriptionList>
        )}

        <DescriptionList style={displayAsList ? SHOW_AS_LIST_STYLE : undefined}>
          {/* Judgment */}
          {"judgment" in log && log.judgment !== null && "spec" in file && "return_type" in file.spec !== null && (
            <>
              <DescriptionTerm>
                <Tag shade="green">Judgment</Tag>
              </DescriptionTerm>

              <Attachment>
                <div className="font-mono">
                  <JudgmentTags evaluatorLog={log} evaluator={file} />
                </div>
              </Attachment>
            </>
          )}

          {/*Stdout */}
          {stdout && (
            <>
              {/* Purposefully dropping the output name part. */}
              <DescriptionTerm></DescriptionTerm>
              <Attachment>
                <div className="font-mono text-12-16 text-text-base-3">
                  <span className="font-bold text-text-base-2">stdout</span>
                  <TruncatedExpandable lines={6} className="mt-4 whitespace-pre-wrap text-text-base-2">
                    {stdout}
                  </TruncatedExpandable>
                </div>
              </Attachment>
            </>
          )}
          {/* Error */}
          {error && (
            <>
              <DescriptionTerm>
                <TagWithTitle shade="red">Error</TagWithTitle>
              </DescriptionTerm>
              <Attachment>
                <div className="font-mono text-12-16 text-text-base-3">
                  <TruncatedExpandable lines={6} className="mt-4 whitespace-pre-wrap text-text-danger-2">
                    {error}
                  </TruncatedExpandable>
                </div>
              </Attachment>
            </>
          )}
        </DescriptionList>
      </div>
    </>
  );
};

const LogTemplate = ({ log }: { log: Log }) => {
  const prompt = getPromptFromLog(log);
  let template = null;
  if (prompt && prompt.endpoint === "complete") {
    /* Fetching the template if it's a prompt log */
    template = prompt.template;
  }

  if (!template) {
    return null;
  }

  return (
    <DescriptionList>
      <DescriptionTerm>
        <Tag>Prompt</Tag>
      </DescriptionTerm>
      <DescriptionDetailsTruncated lines={100}>
        <div className="whitespace-pre-wrap text-13-20">
          <FormattedPrompt
            prompt={(template as string) || ""}
            inputs={log.inputs as Record<string, string>}
            templateToolOutputs={log.template_tool_outputs}
            templateLanguage={prompt?.template_language || "default"}
            // FYI: have chosen not to render the output here, but keep that as a separate 'row'.
          />
        </div>
      </DescriptionDetailsTruncated>
    </DescriptionList>
  );
};

export const LogInputsDisplay = ({
  inputs,
  style,
}: {
  inputs: Record<string, string | number | null>;
  style?: React.CSSProperties;
}) => (
  <DescriptionList style={style}>
    <LogInputsItems inputs={inputs} />
  </DescriptionList>
);

export const LogInputsItems = ({
  inputs,
  descriptionTermClassName,
  descriptionDetailsClassName,
  languageSelectLocation,
}: {
  inputs: Record<string, string | number | null | any>;
  descriptionTermClassName?: string;
  descriptionDetailsClassName?: string;
  languageSelectLocation?: LanguageSelectLocation;
}) => {
  return (
    <>
      {Object.entries(inputs).map(([name, value]) => (
        <LogVariableItem
          key={name}
          name={name}
          value={value}
          descriptionTermClassName={descriptionTermClassName}
          descriptionDetailsClassName={descriptionDetailsClassName}
          languageSelectLocation={languageSelectLocation}
          tagType="input"
        />
      ))}
    </>
  );
};

export const LogToolOutputsDisplay = ({
  templateToolOutputs,
  style,
}: {
  templateToolOutputs: TemplateToolOutput[];
  style?: React.CSSProperties;
}) => {
  const { slug } = useActiveOrganization();
  return (
    <DescriptionList style={style}>
      {templateToolOutputs.map((toolOutput, i) => (
        <LogVariableItem
          key={`${toolOutput.call_signature}-${i}`}
          name={toolOutput.call_signature ?? ""}
          value={toolOutput.output}
          detailsRight={
            <Tooltip content="Open Log in new tab">
              <a
                href={`${getFileHref({ id: toolOutput.file_id, slug, page: "logs" })}?id=${toolOutput.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <EntityLabel entity="log" id={toolOutput.id} size={20} />
              </a>
            </Tooltip>
          }
          tagType="tool"
        />
      ))}
    </DescriptionList>
  );
};

// For inputs and tool outputs
const LogVariableItem = ({
  name,
  value,
  descriptionTermClassName,
  descriptionDetailsClassName,
  languageSelectLocation,
  detailsRight,
  tagType = "input",
}: {
  name: string;
  value: string | number | null | any;
  descriptionTermClassName?: string;
  descriptionDetailsClassName?: string;
  detailsRight?: React.ReactNode;
  languageSelectLocation?: LanguageSelectLocation;
  tagType?: "input" | "tool";
}) => {
  const Tag = tagType === "tool" ? ToolTag : InputTag;
  return (
    <>
      <DescriptionTerm className={classNames("z-10", descriptionTermClassName)}>
        {/* z-index allows the tag to render on top of the description details when user clicks it */}
        <Tag>{name}</Tag>
      </DescriptionTerm>
      <div className="flex items-start gap-8">
        <DescriptionDetails className={classNames("break-words", descriptionDetailsClassName)}>
          <AutoFormattedText
            text={typeof value === "string" ? value : JSON.stringify(value)}
            languageSelectLocation={languageSelectLocation ?? "inside-right"}
          />
        </DescriptionDetails>
        {detailsRight && <div>{detailsRight}</div>}
      </div>
    </>
  );
};
